import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Clock,
  Truck,
  CheckCircle2,
  Search,
  ChevronLeft,
  ChevronRight,
  FileSpreadsheet,
  ScanBarcode,
  Phone,
  MapPin,
  Volume2,
  VolumeX,
  Undo2,
  Check,
  X,
  PackageCheck,
  Package,
  Calendar,
  Sliders,
} from 'lucide-react';
import {
  fetchReturnsAnomalies,
  fetchShippingCarriers,
  scanReturnBarcode,
  unscanReturnBarcode,
} from '../api.js';
import { PageHeader } from '../ui/PageHeader.js';
import { Button } from '../ui/Button.js';
import { Panel } from '../ui/panels.js';
import { TableShell, Table, THead, TBody, Tr, Th, Td } from '../ui/table.js';
import { Loading } from '../ui/feedback.js';
import { formatTnd, playBeep } from '../lib/format.js';



function formatPhone(phone: string | null | undefined): string {
  if (!phone) return '—';
  const clean = phone.trim();
  const digits = clean.replace(/\D/g, '');
  let d8 = digits;
  if (digits.startsWith('216') && digits.length === 11) {
    d8 = digits.slice(3);
  } else if (digits.length === 8) {
    d8 = digits;
  } else if (digits.length > 8) {
    d8 = digits.slice(-8);
  }
  if (d8.length === 8) {
    return `+216 ${d8.slice(0, 2)} ${d8.slice(2, 5)} ${d8.slice(5, 8)}`;
  }
  return clean;
}

export function ReturnsManagementPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const scanInputRef = useRef<HTMLInputElement>(null);
  const filterInputRef = useRef<HTMLInputElement>(null);

  // Scanner states (modeled like Reconcil returns.php)
  const [scanInput, setScanInput] = useState<string>('');
  const [soundEnabled, setSoundEnabled] = useState<boolean>(true);
  const [scanCount, setScanCount] = useState<number>(0);
  const [scanFeedback, setScanFeedback] = useState<{ text: string; ok: boolean } | null>(null);
  const [chips, setChips] = useState<Array<{ code: string; ok: boolean; timestamp: number }>>([]);
  const [lastScannedCode, setLastScannedCode] = useState<string | null>(null);

  // Filters
  const [carrier, setCarrier] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'anomalies' | 'received' | 'pending'>('all');
  const [search, setSearch] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const limit = 25;

  const carriersQuery = useQuery({
    queryKey: ['shipping', 'carriers'],
    queryFn: fetchShippingCarriers,
  });

  const returnsQuery = useQuery({
    queryKey: ['shipping', 'returns-anomalies', carrier, statusFilter, search, page],
    queryFn: () =>
      fetchReturnsAnomalies({
        ...(carrier !== 'all' ? { carrier } : {}),
        statusFilter,
        ...(search.trim() ? { search: search.trim() } : {}),
        page,
        limit,
      }),
  });

  const kpis = returnsQuery.data?.kpis;
  const parcels = returnsQuery.data?.parcels ?? [];
  const totalCount = returnsQuery.data?.totalCount ?? 0;
  const totalPages = returnsQuery.data?.totalPages ?? 1;
  const threshold = returnsQuery.data?.anomalyDaysThreshold ?? 3;

  const totalReturnsAll = kpis?.totalReturns ?? totalCount;
  const receivedCount = kpis?.receivedAtMapara ?? 0;
  const pendingCount = kpis?.agencyReturns ?? 0;
  const overdueCount = kpis?.overdueAgencyReturns ?? 0;
  const pctReceived = totalReturnsAll > 0 ? Math.round((receivedCount / totalReturnsAll) * 100) : 0;

  // Auto-focus scanner on mount
  useEffect(() => {
    scanInputRef.current?.focus();
  }, []);

  // Keyboard navigation shortcuts (F1 Focus, ESC Clear, Ctrl+F Search)
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'F1') {
        e.preventDefault();
        scanInputRef.current?.focus();
        scanInputRef.current?.select();
      } else if (e.key === 'Escape') {
        if (document.activeElement === scanInputRef.current) {
          setScanInput('');
        }
      } else if (e.ctrlKey && (e.key === 'f' || e.key === 'F')) {
        e.preventDefault();
        filterInputRef.current?.focus();
        filterInputRef.current?.select();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Clear feedback after 5s
  useEffect(() => {
    if (!scanFeedback) return;
    const t = setTimeout(() => setScanFeedback(null), 5000);
    return () => clearTimeout(t);
  }, [scanFeedback]);

  // Scan Mutation
  const scanMutation = useMutation({
    mutationFn: (barcode: string) => scanReturnBarcode(barcode),
    onSuccess: (data, scannedCode) => {
      if (soundEnabled) playBeep('success');
      setScanCount((prev) => prev + 1);
      setLastScannedCode(scannedCode);
      const carrierLabel = data.parcel.carrier ? ` — ${data.parcel.carrier.toUpperCase()}` : '';
      setScanFeedback({
        text: `✓ ${scannedCode}${carrierLabel} — Réceptionné chez MaPara (remis en stock)`,
        ok: true,
      });
      setChips((prev) => [{ code: scannedCode, ok: true, timestamp: Date.now() }, ...prev.slice(0, 9)]);
      queryClient.invalidateQueries({ queryKey: ['shipping', 'returns-anomalies'] });
      queryClient.invalidateQueries({ queryKey: ['shipping', 'returns-scanned-today'] });
    },
    onError: (err: unknown, scannedCode) => {
      if (soundEnabled) playBeep('error');
      const msg = err instanceof Error ? err.message : `Colis ${scannedCode} non trouvé`;
      setScanFeedback({
        text: `✗ ${msg}`,
        ok: false,
      });
      setChips((prev) => [{ code: scannedCode, ok: false, timestamp: Date.now() }, ...prev.slice(0, 9)]);
    },
  });

  // Unscan Mutation
  const unscanMutation = useMutation({
    mutationFn: (barcode: string) => unscanReturnBarcode(barcode),
    onSuccess: (_data, code) => {
      if (soundEnabled) playBeep('warning');
      setScanFeedback({
        text: `↩ Réception annulée pour ${code} (remis en attente)`,
        ok: true,
      });
      setChips((prev) => prev.filter((c) => c.code !== code));
      queryClient.invalidateQueries({ queryKey: ['shipping', 'returns-anomalies'] });
    },
    onError: (err: unknown, code) => {
      const msg = err instanceof Error ? err.message : `Erreur annulation ${code}`;
      setScanFeedback({
        text: `✗ ${msg}`,
        ok: false,
      });
    },
  });

  const handleScanSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const clean = scanInput.trim();
    if (!clean) return;
    setScanInput('');
    scanMutation.mutate(clean);
    scanInputRef.current?.focus();
  };

  return (
    <div className="space-y-6 animate-fade">
      <PageHeader
        index="S37"
        eyebrow="Gestion Logistique · Retours & Délais"
        title="Console de Réception & Gestion des Retours"
        purpose="Console unifiée de scan des retours transporteur, réintégration au stock MaPara (État 30) et surveillance des anomalies de délai (+3 jours)."
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSoundEnabled((v) => !v)}
              className="p-2 rounded-lg border border-border bg-card text-ink-soft hover:text-ink transition-colors shadow-2xs"
              title={soundEnabled ? 'Désactiver les signaux sonores' : 'Activer les signaux sonores'}
            >
              {soundEnabled ? <Volume2 className="h-4 w-4 text-emerald-700" /> : <VolumeX className="h-4 w-4 text-ink-ghost" />}
            </button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (parcels.length === 0) return;
                const headers = ['Code-barre', 'Client', 'Telephone', 'Ville', 'Montant', 'Presta', 'Etat', 'JoursEnRetour', 'Anomalie'];
                const rows = parcels.map((p) => [
                  p.codeBarre,
                  `"${p.clientName ?? (p as unknown as Record<string, unknown>).nom ?? ''}"`,
                  `"${p.clientPhone ?? (p as unknown as Record<string, unknown>).tel ?? ''}"`,
                  `"${p.location ?? (p as unknown as Record<string, unknown>).ville ?? ''}"`,
                  p.shiparonPrice ?? (p as unknown as Record<string, unknown>).prix ?? '',
                  p.carrier ?? (p as unknown as Record<string, unknown>).presta ?? '',
                  `"${p.shiparonEtatLabel ?? ''}"`,
                  p.daysInReturn ?? 0,
                  p.isAnomaly ? 'OUI' : 'NON',
                ]);
                const csvContent = [headers.join(';'), ...rows.map((r) => r.join(';'))].join('\n');
                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `retours_mapara_${new Date().toISOString().slice(0, 10)}.csv`;
                a.click();
              }}
              className="text-ink-soft hover:text-ink"
            >
              <FileSpreadsheet className="h-4 w-4 mr-1.5" />
              Exporter CSV
            </Button>
          </div>
        }
      />
      
      {/* Reconcil-Style Active Scanner Console Box */}
      <Panel className="p-5 border-emerald-600/30 bg-gradient-to-b from-emerald-500/5 via-card to-card shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-border/80">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-emerald-600 text-white shadow-xs">
              <ScanBarcode className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-ink flex items-center gap-2">
                Scanner un retour colis
                <span className="text-xs font-normal text-ink-soft">
                  (Flasheur douchette ou saisie manuelle)
                </span>
              </h3>
              <p className="text-xs text-ink-soft">
                Passe immédiatement le colis en <strong>État 30 (Rtn reçu)</strong> et valide la réintégration en stock.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 self-start sm:self-center">
            <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-900 border border-emerald-300">
              {receivedCount.toLocaleString('fr-FR')} / {totalReturnsAll.toLocaleString('fr-FR')} reçus
            </span>
            <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-cream text-ink-soft border border-border">
              {scanCount} scanné(s) cette session
            </span>
          </div>
        </div>

        {/* Large Scanner Input */}
        <form onSubmit={handleScanSubmit} className="space-y-3">
          <div className="relative">
            <input
              ref={scanInputRef}
              type="text"
              value={scanInput}
              onChange={(e) => setScanInput(e.target.value)}
              placeholder="Flasher ou saisir le code-barre retour (ex: JP123456789)... [F1]"
              autoComplete="off"
              className="w-full text-base sm:text-lg font-mono tracking-wider px-4 py-3.5 rounded-xl border-2 border-emerald-600/40 bg-card text-ink placeholder:text-ink-ghost focus:outline-none focus:border-emerald-600 focus:ring-4 focus:ring-emerald-500/15 transition-all shadow-inner"
            />
            <Button
              type="submit"
              variant="primary"
              size="md"
              disabled={!scanInput.trim() || scanMutation.isPending}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-emerald-700 hover:bg-emerald-800 text-white font-bold px-4 py-2"
            >
              {scanMutation.isPending ? 'Enregistrement…' : 'Valider Scan [Enter]'}
            </Button>
          </div>

          {/* Feedback message banner */}
          {scanFeedback && (
            <div
              className={`p-3 rounded-lg text-xs font-semibold flex items-center justify-between gap-3 animate-fade ${
                scanFeedback.ok
                  ? 'bg-emerald-100 text-emerald-950 border border-emerald-400'
                  : 'bg-rust-100 text-rust-950 border border-rust-400'
              }`}
            >
              <div className="flex items-center gap-2">
                {scanFeedback.ok ? <Check className="h-4 w-4 text-emerald-700 shrink-0" /> : <X className="h-4 w-4 text-rust-700 shrink-0" />}
                <span>{scanFeedback.text}</span>
              </div>
              <button
                type="button"
                onClick={() => setScanFeedback(null)}
                className="text-xs opacity-60 hover:opacity-100"
              >
                Fermer
              </button>
            </div>
          )}

          {/* Scanned Barcodes Chips Bar */}
          {chips.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap pt-1">
              <span className="text-[11px] font-semibold text-ink-soft">Derniers scans :</span>
              {chips.map((c) => (
                <span
                  key={`${c.code}-${c.timestamp}`}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-mono font-medium border transition-colors ${
                    c.ok
                      ? 'bg-emerald-50 text-emerald-900 border-emerald-200'
                      : 'bg-rust-50 text-rust-900 border-rust-200'
                  }`}
                >
                  {c.ok ? '✓' : '✗'} {c.code}
                  {c.ok && (
                    <button
                      type="button"
                      onClick={() => unscanMutation.mutate(c.code)}
                      disabled={unscanMutation.isPending}
                      className="ml-1 text-ink-ghost hover:text-rust-700 p-0.5 rounded hover:bg-rust-100 transition-colors"
                      title="Annuler cette réception"
                    >
                      <Undo2 className="h-3 w-3" />
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}

          {/* Keyboard Shortcuts Legend (Like Reconcil kbd-bar) */}
          <div className="flex items-center gap-4 flex-wrap pt-2 border-t border-border/60 text-[11px] text-ink-soft">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 rounded bg-cream font-mono text-[10px] text-ink border border-border">F1</kbd>
              Focus Scanner
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 rounded bg-cream font-mono text-[10px] text-ink border border-border">ESC</kbd>
              Effacer Saisie
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 rounded bg-cream font-mono text-[10px] text-ink border border-border">Ctrl+F</kbd>
              Filtrer Tableau
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 rounded bg-cream font-mono text-[10px] text-ink border border-border">Enter</kbd>
              Enregistrer Réception
            </span>
          </div>
        </form>
      </Panel>

      {/* Return Start Date Scope Notice */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-3.5 py-2.5 rounded-xl bg-cream/70 border border-border/80 text-xs">
        <div className="flex items-center gap-2 text-ink-soft">
          <Calendar className="h-4 w-4 text-emerald-700 shrink-0" />
          <span>
            Périmètre de surveillance des retours :{' '}
            <strong className="text-ink">
              {returnsQuery.data?.returnStartDate
                ? `Depuis le ${new Date(returnsQuery.data.returnStartDate).toLocaleDateString('fr-FR')}`
                : 'Tout l\'historique'}
            </strong>
          </span>
        </div>
        <Link
          to="/shipping/config"
          className="text-emerald-700 hover:text-emerald-800 font-semibold inline-flex items-center gap-1 hover:underline text-[11px]"
        >
          <Sliders className="h-3 w-3" />
          Modifier la date de début dans la configuration
        </Link>
      </div>

      {/* KPI Cards & Progress Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Panel className="p-4 border-border bg-card shadow-xs">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-slate-100 text-slate-700">
              <Package className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs text-ink-soft">Total Colis en Retour</p>
              <p className="text-xl font-bold text-ink">{totalReturnsAll.toLocaleString('fr-FR')}</p>
            </div>
          </div>
        </Panel>

        <Panel className="p-4 border-emerald-300 bg-emerald-50/40 shadow-xs">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-emerald-100 text-emerald-800">
              <PackageCheck className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs text-emerald-800 font-medium">Reçus MaPara (État 30)</p>
              <p className="text-xl font-bold text-emerald-900">{receivedCount.toLocaleString('fr-FR')}</p>
            </div>
          </div>
        </Panel>

        <Panel className="p-4 border-amber-300 bg-amber-50/40 shadow-xs">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-amber-100 text-amber-800">
              <Clock className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs text-amber-800 font-medium">En Attente Agence / Hub</p>
              <p className="text-xl font-bold text-amber-900">{pendingCount.toLocaleString('fr-FR')}</p>
            </div>
          </div>
        </Panel>

        <Panel className="p-4 border-rust-300 bg-rust-50/50 shadow-xs">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-rust-100 text-rust-800">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs text-rust-800 font-medium">Anomalies (+{threshold}j)</p>
              <p className="text-xl font-bold text-rust-950">{overdueCount.toLocaleString('fr-FR')}</p>
            </div>
          </div>
        </Panel>
      </div>

      {/* Progress Bar (Reconcil returns-progress) */}
      <Panel className="p-4 border-border bg-card shadow-xs space-y-2">
        <div className="flex items-center justify-between text-xs">
          <span className="font-semibold text-ink">Progression du Rapatriement des Retours</span>
          <span className="font-mono font-bold text-emerald-800">{pctReceived}% réceptionnés ({receivedCount} / {totalReturnsAll})</span>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden p-0.5 border border-border/60">
          <div
            className="bg-gradient-to-r from-emerald-600 to-emerald-500 h-full rounded-full transition-all duration-500"
            style={{ width: `${pctReceived}%` }}
          />
        </div>
      </Panel>

      {/* Filters & Interactive Returns Table */}
      <Panel>
        <div className="p-4 flex flex-wrap items-center justify-between gap-3 border-b border-border">
          <div className="flex flex-wrap items-center gap-3">
            {/* Status Filter Tabs (Tous / Anomalies / Reçus / En attente) */}
            <div className="flex items-center bg-cream/70 p-1 rounded-lg border border-border/80">
              <button
                type="button"
                onClick={() => {
                  setStatusFilter('all');
                  setPage(1);
                }}
                className={`px-3 py-1 text-xs rounded-md font-semibold transition-all ${
                  statusFilter === 'all'
                    ? 'bg-card text-ink shadow-2xs font-bold border border-border'
                    : 'text-ink-soft hover:text-ink'
                }`}
              >
                Tous les Retours ({totalReturnsAll.toLocaleString('fr-FR')})
              </button>
              <button
                type="button"
                onClick={() => {
                  setStatusFilter('anomalies');
                  setPage(1);
                }}
                className={`px-3 py-1 text-xs rounded-md font-semibold transition-all ${
                  statusFilter === 'anomalies'
                    ? 'bg-rust-700 text-white shadow-2xs'
                    : 'text-rust-700 hover:bg-rust-50'
                }`}
              >
                🚨 Anomalies (+{threshold}j)
              </button>
              <button
                type="button"
                onClick={() => {
                  setStatusFilter('received');
                  setPage(1);
                }}
                className={`px-3 py-1 text-xs rounded-md font-semibold transition-all ${
                  statusFilter === 'received'
                    ? 'bg-emerald-700 text-white shadow-2xs'
                    : 'text-emerald-700 hover:bg-emerald-50'
                }`}
              >
                ✓ Reçus MaPara ({receivedCount})
              </button>
              <button
                type="button"
                onClick={() => {
                  setStatusFilter('pending');
                  setPage(1);
                }}
                className={`px-3 py-1 text-xs rounded-md font-semibold transition-all ${
                  statusFilter === 'pending'
                    ? 'bg-amber-600 text-white shadow-2xs'
                    : 'text-amber-700 hover:bg-amber-50'
                }`}
              >
                ● En Attente ({pendingCount})
              </button>
            </div>

            {/* Search */}
            <div className="relative min-w-[220px]">
              <Search className="h-4 w-4 text-ink-ghost absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                ref={filterInputRef}
                type="text"
                placeholder="Filtrer code-barre, client, tel… (Ctrl+F)"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full text-xs pl-9 pr-3 py-1.5 rounded-lg border border-border bg-card text-ink focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 outline-none"
              />
            </div>

            {/* Carrier select */}
            <select
              value={carrier}
              onChange={(e) => {
                setCarrier(e.target.value);
                setPage(1);
              }}
              className="text-xs rounded-lg px-2.5 py-1.5 border border-border bg-card text-ink font-medium cursor-pointer"
            >
              <option value="all">Tous transporteurs</option>
              {carriersQuery.data?.carriers.map((c) => (
                <option key={c.carrier} value={c.carrier}>
                  {c.carrier} ({c.count})
                </option>
              ))}
            </select>
          </div>

          <div className="text-xs text-ink-soft">
            <strong>{totalCount.toLocaleString('fr-FR')}</strong> colis retour trouvé(s)
          </div>
        </div>

        {/* Table of returns */}
        <TableShell>
          <Table>
            <THead>
              <Tr>
                <Th className="w-[130px]">Statut</Th>
                <Th>Code-Barres & Facture</Th>
                <Th>Client & Contact</Th>
                <Th>Destination</Th>
                <Th>Montant COD</Th>
                <Th>Transporteur</Th>
                <Th>Délai en Retour</Th>
                <Th className="text-right">Action Réception</Th>
              </Tr>
            </THead>
            <TBody>
              {returnsQuery.isLoading ? (
                <Tr>
                  <Td colSpan={8} className="text-center py-8">
                    <Loading label="Chargement des retours colis…" />
                  </Td>
                </Tr>
              ) : parcels.length === 0 ? (
                <Tr>
                  <Td colSpan={8} className="text-center py-8 text-ink-soft text-xs">
                    Aucun colis retour correspondant aux filtres sélectionnés.
                  </Td>
                </Tr>
              ) : (
                parcels.map((parcel) => {
                  const isRec =
                    (parcel as unknown as Record<string, unknown>).etat === 30 ||
                    parcel.shiparonEtatLabel === 'Rtn reçu';
                  const isOverdue = !isRec && Boolean(parcel.isAnomaly);
                  const isHighlighted = lastScannedCode === parcel.codeBarre;

                  const clientName = parcel.clientName || (parcel as unknown as Record<string, unknown>).nom as string || 'Client';
                  const clientPhone = parcel.clientPhone || (parcel as unknown as Record<string, unknown>).tel as string || null;
                  const location = parcel.location || (parcel as unknown as Record<string, unknown>).ville as string || '—';
                  const price = parcel.shiparonPrice ?? (parcel as unknown as Record<string, unknown>).prix as number ?? null;
                  const presta = parcel.carrier || (parcel as unknown as Record<string, unknown>).presta as string || 'Non assigné';

                  return (
                    <Tr
                      key={parcel.codeBarre}
                      className={`transition-colors ${
                        isHighlighted
                          ? 'bg-emerald-100/70 font-semibold'
                          : isOverdue
                          ? 'bg-rust-50/60 hover:bg-rust-50'
                          : isRec
                          ? 'bg-emerald-50/30 hover:bg-emerald-50/50'
                          : 'hover:bg-cream/40'
                      }`}
                    >
                      <Td>
                        {isRec ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-900 border border-emerald-300">
                            <CheckCircle2 className="h-3 w-3 text-emerald-700" />
                            ✓ Reçu MaPara
                          </span>
                        ) : isOverdue ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-rust-100 text-rust-900 border border-rust-300">
                            <AlertTriangle className="h-3 w-3 text-rust-700" />
                            🚨 Retard +{parcel.daysInReturn}j
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-900 border border-amber-300">
                            <Clock className="h-3 w-3 text-amber-700" />
                            ● En attente
                          </span>
                        )}
                      </Td>
                      <Td className="font-mono font-bold text-xs text-ink">
                        <span>{parcel.codeBarre}</span>
                        {parcel.facture && (
                          <span className="text-[11px] text-ink-soft font-normal block font-sans">
                            Facture: {parcel.facture}
                          </span>
                        )}
                      </Td>
                      <Td className="text-xs">
                        <p className="font-semibold text-ink">{clientName}</p>
                        {clientPhone && (
                          <p className="text-[11px] text-ink-soft flex items-center gap-1 mt-0.5 font-mono">
                            <Phone className="h-3 w-3 text-ink-ghost" />
                            {formatPhone(clientPhone)}
                          </p>
                        )}
                      </Td>
                      <Td className="text-xs text-ink-soft">
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3 text-ink-ghost shrink-0" />
                          {location}
                        </span>
                      </Td>
                      <Td className="text-xs font-bold text-ink whitespace-nowrap">
                        {formatTnd(price)}
                      </Td>
                      <Td className="text-xs font-medium text-ink">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-cream text-ink border border-border/80">
                          <Truck className="h-3 w-3 text-ink-soft" />
                          {presta}
                        </span>
                      </Td>
                      <Td className="text-xs font-mono">
                        {parcel.daysInReturn !== undefined && parcel.daysInReturn > 0 ? (
                          <span className={isOverdue ? 'font-bold text-rust-800' : 'text-ink-soft'}>
                            {parcel.daysInReturn} jour(s)
                          </span>
                        ) : (
                          <span className="text-ink-faint">—</span>
                        )}
                      </Td>
                      <Td className="text-right">
                        {isRec ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => unscanMutation.mutate(parcel.codeBarre)}
                            disabled={unscanMutation.isPending}
                            className="text-xs text-ink-soft hover:text-rust-700 hover:border-rust-300 shadow-2xs"
                          >
                            <Undo2 className="h-3 w-3 mr-1" />
                            Annuler scan
                          </Button>
                        ) : (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => scanMutation.mutate(parcel.codeBarre)}
                            disabled={scanMutation.isPending}
                            className="text-xs bg-emerald-700 hover:bg-emerald-800 text-white font-semibold shadow-2xs"
                          >
                            <ScanBarcode className="h-3 w-3 mr-1" />
                            Flasher reçu
                          </Button>
                        )}
                      </Td>
                    </Tr>
                  );
                })
              )}
            </TBody>
          </Table>
        </TableShell>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-border flex items-center justify-between text-xs">
            <span className="text-ink-soft">
              Page {page} sur {totalPages}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-4 w-4 mr-1" /> Précédent
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Suivant <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </Panel>
    </div>
  );
}
